/**
 * What is left of the hand-rolled record lists: the "View all" link they all
 * ended with, and the visit-status colour map.
 *
 * The lists themselves are `RelatedList` now — the real list engine, with the
 * real controls — so a property's visits filter, group, sort and summarise
 * exactly like the Jobs board does. This file is the two pieces of that
 * arrangement which are not the table: where "View all" goes, and which job
 * colour a visit status borrows.
 */
import { useNavigate } from 'react-router-dom'
import { encodeView, emptyView, type FilterCondition } from '@rowted/shared'

/**
 * Where "View all" goes: a list screen, narrowed to this record.
 *
 * The filter is the real query grammar, encoded the way the list engine puts a
 * view in the URL — so the screen that opens is not a special case, it is the
 * ordinary screen with a filter chip somebody can take off.
 */
export function viewAllHref(area: string, conditions: FilterCondition[]): string {
  const view = { ...emptyView(), filter: { match: 'and' as const, conditions } }
  return `/${area}?view=${encodeView(view)}`
}

export function ViewAllLink({ href, label = 'View all' }: { href: string; label?: string }) {
  const navigate = useNavigate()
  return (
    <button type="button" onClick={() => navigate(href)} className="text-accent hover:underline">
      {label}
    </button>
  )
}

/*
 * Visit statuses have no semantic pair of their own in design/tokens.json, so
 * they borrow the job pair that means the same thing. Inventing colours here
 * would put them outside the token pipeline, which derives the dark variants
 * and checks contrast; an entry that skipped both would fail neither test and
 * still be wrong.
 */
export const VISIT_STATUS_AS_JOB: Record<string, string> = {
  planned: 'requested',
  scheduled: 'scheduled',
  dispatched: 'scheduled',
  in_progress: 'in_progress',
  completed: 'completed',
  skipped: 'cancelled',
  cancelled: 'cancelled',
}
